import { getInitData } from './telegram';

/**
 * HTTP-клиент. Знает только про наш API и Bearer-токен — про Telegram здесь
 * ровно одна строка (получение initData для логина), поэтому в Phase 2 клиент
 * переиспользуется нативными приложениями без изменений.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
const TOKEN_STORAGE_KEY = 'marketplace.tokens';

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function apiErrorMessage(cause: unknown, fallback = 'Ошибка запроса'): string {
  if (!(cause instanceof ApiError)) {
    return cause instanceof Error ? cause.message : fallback;
  }

  const fieldErrors = (cause.details as { fieldErrors?: Record<string, string[] | undefined> } | undefined)
    ?.fieldErrors;
  const parts = fieldErrors
    ? Object.entries(fieldErrors).flatMap(([field, messages]) =>
        (messages ?? []).map((message) => `${field}: ${message}`),
      )
    : [];

  return parts.length > 0 ? parts.join('; ') : cause.message;
}

interface StoredTokens {
  accessToken: string;
  refreshToken: string;
}

function readTokens(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredTokens) : null;
  } catch {
    return null;
  }
}

function writeTokens(tokens: StoredTokens | null): void {
  try {
    if (tokens) {
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch {
    // Приватный режим / отключённое хранилище: работаем на токене в памяти.
  }
}

let tokens: StoredTokens | null = readTokens();
/** Один общий промис логина, чтобы параллельные 401 не устроили шторм /auth/session. */
let sessionPromise: Promise<void> | null = null;

export interface SessionUser {
  id: string;
  role: 'USER' | 'ADMIN';
  displayName: string | null;
  username: string | null;
  avatarUrl: string | null;
  starBalance: number;
}

let sessionUser: SessionUser | null = null;

/**
 * Профиль из последнего ответа на логин. Нужен, чтобы решить, показывать ли
 * вкладку админки, не делая для этого отдельный запрос.
 *
 * Роль здесь — подсказка для интерфейса, а не средство защиты: доступ к данным
 * проверяет бэкенд по роли внутри JWT. Подмена значения в консоли покажет
 * пустую вкладку с 403, а не чужие данные.
 */
export function getSessionUser(): SessionUser | null {
  return sessionUser;
}

async function requestSession(): Promise<void> {
  const initData = getInitData();

  if (!initData) {
    throw new ApiError(401, 'NO_INIT_DATA', 'Приложение нужно открыть из Telegram');
  }

  const response = await fetch(`${BASE_URL}/auth/session`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ provider: 'TELEGRAM', credential: initData }),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(response.status, body?.error.code ?? 'AUTH_FAILED', body?.error.message ?? 'Не удалось войти');
  }

  const session = (await response.json()) as StoredTokens & { user: SessionUser };
  tokens = { accessToken: session.accessToken, refreshToken: session.refreshToken };
  sessionUser = session.user;
  writeTokens(tokens);
}

/** Логин/релогин. Повторные параллельные вызовы переиспользуют один запрос. */
export function ensureSession(): Promise<void> {
  sessionPromise ??= requestSession().finally(() => {
    sessionPromise = null;
  });

  return sessionPromise;
}

async function tryRefresh(): Promise<boolean> {
  if (!tokens?.refreshToken) return false;

  const response = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken: tokens.refreshToken }),
  });

  if (!response.ok) {
    tokens = null;
    sessionUser = null;
    writeTokens(null);
    return false;
  }

  const session = (await response.json()) as StoredTokens & { user?: SessionUser };
  tokens = { accessToken: session.accessToken, refreshToken: session.refreshToken };
  if (session.user) sessionUser = session.user;
  writeTokens(tokens);
  return true;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Публичные эндпоинты (каталог) не требуют токена. */
  auth?: boolean;
}

/**
 * Основной метод. На 401 один раз пробует refresh, потом полный релогин по
 * initData, и только затем сдаётся. Сессия короткая (15 минут), а Mini App
 * живёт в фоне долго — без этого пользователь ловил бы случайные выходы.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, auth = true, headers, ...rest } = options;

  const send = async (): Promise<Response> => {
    const finalHeaders = new Headers(headers);

    if (body !== undefined) {
      finalHeaders.set('content-type', 'application/json');
    }

    if (auth && tokens?.accessToken) {
      finalHeaders.set('authorization', `Bearer ${tokens.accessToken}`);
    }

    return fetch(`${BASE_URL}${path}`, {
      ...rest,
      headers: finalHeaders,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  };

  if (auth && !tokens) {
    await ensureSession();
  }

  let response = await send();

  if (response.status === 401 && auth) {
    const refreshed = await tryRefresh();

    if (!refreshed) {
      await ensureSession();
    }

    response = await send();
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = (await response.json().catch(() => null)) as unknown;

  if (!response.ok) {
    const error = (payload as ApiErrorBody | null)?.error;
    throw new ApiError(response.status, error?.code ?? 'UNKNOWN', error?.message ?? 'Ошибка запроса', error?.details);
  }

  return payload as T;
}

export async function apiFetchBlob(path: string): Promise<Blob> {
  if (!tokens) {
    await ensureSession();
  }

  const send = async (): Promise<Response> => {
    const headers = new Headers();

    if (tokens?.accessToken) {
      headers.set('authorization', `Bearer ${tokens.accessToken}`);
    }

    return fetch(`${BASE_URL}${path}`, { headers });
  };

  let response = await send();

  if (response.status === 401) {
    const refreshed = await tryRefresh();

    if (!refreshed) {
      await ensureSession();
    }

    response = await send();
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      payload?.error.code ?? 'UNKNOWN',
      payload?.error.message ?? 'Не удалось загрузить файл',
    );
  }

  return response.blob();
}

export function clearSession(): void {
  tokens = null;
  sessionUser = null;
  writeTokens(null);
}
